/**
 * How a department's headcount reads. One formatter, because B3 asks for the
 * split "in both places it appears" — the Settings tab and the onboarding
 * departments card — and two hand-written versions would drift.
 *
 * Level comes from the person dossier (`q2_level`), which is only filled once
 * somebody states it, so a department very often has people whose level nobody
 * has said yet. Those are named rather than folded into operational: the parts
 * have to add up, and guessing that an unstated level means individual
 * contributor would quietly overstate how junior a team is.
 */

export interface DepartmentCounts {
  members: number;
  leadership?: number;
  operational?: number;
  level_unknown?: number;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "12 people" — always available. */
export function totalLine(d: DepartmentCounts): string {
  return plural(d.members ?? 0, 'person', 'people');
}

/** "2 leadership · 10 operational · 1 level not set", or null when nothing is known. */
export function splitLine(d: DepartmentCounts): string | null {
  const lead = d.leadership ?? 0;
  const ops = d.operational ?? 0;
  const unknown = d.level_unknown ?? 0;
  if (lead === 0 && ops === 0) {
    // Nobody's level is known. Saying "0 leadership · 0 operational" would read
    // as a fact about the team rather than a gap in what we hold.
    return unknown > 0 ? `${unknown} awaiting a stated level` : null;
  }
  const parts = [`${lead} leadership`, `${ops} operational`];
  if (unknown > 0) parts.push(`${unknown} level not set`);
  return parts.join(' · ');
}
